/**
 * This class has non-react methods that are better stored here so they can be re-used by both react and non-react code.
 */
export default class ApiUtils {
  static buildProviderAlias(linkedTitle) {
    const parser = new DOMParser();
    const linkElement = parser.parseFromString(linkedTitle, 'text/html');
    return linkElement.body.firstElementChild.href;
  }

  static async searchLocationByNid(nid) {
    const endpoint = `/${
      window.location.pathname.split('/')[1]
    }/search-location/rest?search=&nid=${nid}&page=0`;

    return ApiUtils.fetchAllRestAPIPages(endpoint);
  }

  static async getProviderByNPIAndMapToConfirmation(npi) {
    // get provider json api to pass to confirmation
    const provObj = (await ApiUtils.searchProvidersByNpi(npi)).rows;

    if (provObj.length > 0) {
      return ApiUtils.mapFieldsToConfirmationFormat(provObj[0]);
    }
    return null;
  }

  static mapFieldsToConfirmationFormat(objectToMap) {
    return {
      field_provider_npi_number: objectToMap.field_provider_npi_number,
      field_hero_banner: {
        field_hero_banner_text: {
          value: objectToMap.field_hero_banner_text_1,
        },
        field_media_image: {
          field_media_image: {
            image_style_uri: {
              webp: objectToMap.hero_url,
            },
          },
        },
        field_is_luminis_health_provider:
          objectToMap.field_is_luminis_health_provider === '1',
        field_ratings_label: {
          field_label: objectToMap.field_ratings_label,
        },
        field_no_ratings_link: {
          field_link_block_link: {
            uri: objectToMap.field_no_rating_link,
            title: objectToMap.field_no_rating_text,
          },
        },
        field_luminis_health_provider_la: {
          field_label: objectToMap.field_is_luminis_health_provider_label,
        },
        field_luminis_health_provider_to: {
          field_label: objectToMap.field_luminis_health_provider_to,
        },
        field_affiliated_provider_label: {
          field_label: objectToMap.field_affiliated_provider_label,
        },
        field_affiliate_provider_toolti2: {
          field_label: objectToMap.field_affiliated_provider_tooltip,
        },
        field_offering_telehealth_label: {
          field_label: objectToMap.field_offering_telehealth_label,
        },
        field_accepting_new_patients_lab: {
          field_label: objectToMap.field_accepting_new_patients_lab,
        },
      },
      field_specialty_list: objectToMap.field_specialty_list,
      field_subspecialty_list: objectToMap.field_subspecialty_list,
      path: {
        alias: ApiUtils.buildProviderAlias(objectToMap.title),
      },
      field_offering_telehealth: objectToMap.field_offering_telehealth === '1',
      field_accepting_new_patients:
        objectToMap.field_accepting_new_patients === '1',
    };
  }

  static async searchProvidersByNpi(npis) {
    let npiString = '';
    if (typeof npis === 'string') {
      npiString = npis;
    } else {
      npiString = ApiUtils.createUrlFriendlyStringParamsFromArray(npis);
    }

    return ApiUtils.fetchAllRestAPIPages(`/${
      window.location.pathname.split('/')[1]
    }/search-provider/rest?search=&npi=${npiString}`);
  }

  static createUrlFriendlyStringParamsFromArray(array) {
    let paramString = '';
    array.forEach(item => {
      if (paramString.length > 0) {
        paramString = `${paramString}+${item}`;
      } else {
        paramString = `${item}`;
      }
    });

    return paramString;
  }

  static async makeMnemonicHumanReadable(mnemonic) {
    if (mnemonic) {
      const jsonapiQuery = `/${
        window.location.pathname.split('/')[1]
      }/jsonapi/taxonomy_term/visit_types?filter[field_mnemonic][condition][path]=field_mnemonic&filter[field_mnemonic][condition][operator]==&filter[field_mnemonic][condition][value]=${mnemonic}`;

      try {
        const responseQuery = await fetch(jsonapiQuery, {
          method: 'GET',
        });

        if (!responseQuery.ok) {
          throw new Error(
            `Server responded with status code ${responseQuery.status}`,
          );
        }

        const response = await responseQuery.json();

        if (response.data && response.data[0]) {
          return response.data[0].attributes.name;
        }
      } catch {
        return {
          statusCode: 'Bad Request',
          message: 'Error fetching data',
        };
      }

      // fall back to string manipulation
      const withSpaces = mnemonic.replace(/_/g, ' ');
      return withSpaces.replaceAll(
        /\S*/g,
        word => `${word.slice(0, 1)}${word.slice(1).toLowerCase()}`,
      );
    }
  }

  static getLocationFromURL() {
    const urlParams = new URLSearchParams(
      window.location.href.substring(window.location.href.indexOf('?')),
    );

    return urlParams.get('location');
  }

  static async fetchJSONApiRequest(url) {
    try {
      // Fetch request and parse as JSON
      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(`Server responded with status code ${response.status}`);
      }

      // Extract the url of the response's "next" relational Link header
      let next;
      if (data.links.next !== undefined) {
        // Cloud IDE apparently has a pathname and search subpath that regular acquia does not.
        if (data.links.next.pathname) {
          next = data.links.next.pathname + data.links.next.search;
        } else {
          next = data.links.next.href;
        }
      }

      // If another page exists, merge its output into the array recursively
      if (next) {
        const fetched = await ApiUtils.fetchJSONApiRequest(next);
        data.data = [...data.data, ...fetched.data];
        if (fetched.included) {
          data.included = [...data.included, ...fetched.included];
        }
      }
      return data;
    } catch {
      // needed for testing
    }
  }

  static fetchAllRestAPIPages(searchStringWithoutPage) {
    const currentPage = 0;
    let allResults = [];

    function fetchPage(page) {
      return fetch(`${searchStringWithoutPage}&page=${page}`)
        .then(res => res.json())
        .then(
          results => {
            allResults = allResults.concat(results || []);
            const { pager } = results;

            // Check if there are more pages to fetch
            if (pager && pager.current_page < pager.total_pages - 1) {
              // Fetch the next page
              return fetchPage(pager.current_page + 1);
            }
            // Finalize results: if only one item, return it as an object
            return allResults.length === 1 ? allResults[0] : allResults;
          },
          error => {
            // eslint-disable-next-line no-console
            console.error(error);
            throw error;
          }
        );
    }

    return fetchPage(currentPage);
  }


  static getProviderSpecialtiesArray(data) {
    if (
      !data ||
      (data.field_specialty_list === null &&
        data.field_subspecialty_list === null)
    )
      return '';

    const getTitles = list => {
      if (!Array.isArray(list)) {
        if (typeof list === 'string') {
          return list;
        }
        return '';
      }
      return list
        .map(item => (item ? item.field_title : null))
        .filter(item => item !== null)
        .join(', ');
    };

    const specialties = getTitles(data.field_specialty_list);
    const subspecialties = getTitles(data.field_subspecialty_list);

    if (subspecialties && specialties) {
      return `${specialties}, ${subspecialties}`;
    }
    return specialties || subspecialties || '';
  }
}
